import { IResultMessage } from "@multiweb/types/contracts";
import { ApplicationTheme } from "UI/Shared/ApplicationTheme";
import { InjectedProperty } from "../../../Global/Base/DIContainer";
import { Event } from "../../../Global/Base/Events";
import { GlobalContext } from "../../../Global/Base/GlobalContext";
import { IContentBuilder } from "./DataBuilders/IContentBuilder";
import { ILoader } from "./ILoader";
import { MessageButtonType, MessageWindow } from "./MessageWindow";
import {
    IMainMenu,
} from "./UIManagerInterfaces";

export interface MessageButtonResult {
    result: MessageButtonType;
}

/**
 * @includeToDoc
 */
export interface IUIManagerWorker extends ILoader {
    blockingLayer: HTMLElement;

    // /** Událost o změně stavu skrytí tlačítka pro zavření formuláře */
    // readonly formHideCloseButtonChanged: Event<IUIManagerWorker, IHideCloseButtonChanged>;
    /** Událost o kliku na vrstvu překrývající formulář při otevřeném menu */
    readonly menuOverlayClicked: Event<IUIManagerWorker, MouseEvent>;

    /**
     * Aktuálně nastavené téma aplikace
     */
    readonly applicationTheme: ApplicationTheme | undefined;

    init(): void;

    /**
     * Stará se o načtení definice formuláře ze serveru.
     * @param formName Kód formuláře k otevření.
     * @param previewId ID pod kterým se má na serveru loadovat preview definice formuláře.
     * @returns HTML element s formulářem.
     */
    loadFormDefinition(formName: string, previewId?: string): Promise<HTMLElement>;

    showMessage(msgWin: MessageWindow): Promise<MessageButtonResult>;

    showOverlay(visible: boolean, formsPlaceOnly?: boolean): void;
    showResultMessages(messages: readonly IResultMessage[] | null | undefined): Promise<void>;

    generateElementID(): string;

    loadUIHistoryState(state: UIHistoryState): void;
    addUIHistory(state: UIHistoryState, title: string): void;

    showBlockingLayer(content?: IContentBuilder): void;
    hideBlockingLayer(): void;

    /**
     * Zobrazení vrstvy překrývající formulář při otevřeném menu
     */
    showMenuOverlay(): void;
    /**
     * Skrytí vrstvy překrývající formulář při otevřeném menu
     */
    hideMenuOverlay(): void;

    /**
     * Nastaví téma aplikace
     * @param theme Téma, které se má nastavit
     */
    applyApplicationTheme(theme: ApplicationTheme): void;

    /**Událost změny velikosti okna, prostoru pro formuláře nebo frame */
    windowResized: Event<any, any>;

    /**
     * Spustí sledování změn velikosti HTML elementu
     * @param element Sledovaný element
     * @param resizeListener Metoda zpracovávající resize
     */
    resizeObserve(element: HTMLElement, resizeListener: () => void): void;

    /**
     * Zastaví sledování změn velikosti HTML elementu
     * @param element Sledovaný element
     * @param resizeListener Metoda zpracovávající resize
     */
    resizeUnobserve(element: HTMLElement, resizeListener: () => void): void;
}

export class IUIManagerWorker {
    private static _properties: (keyof IUIManagerWorker)[] = [
        "blockingLayer", "menuOverlayClicked", "applicationTheme",
    ];

    private static _methods: (keyof IUIManagerWorker)[] = [
        "init", "loadFormDefinition", "showMessage",
        "showOverlay", "showResultMessages", "showResultMessages",
        "generateElementID", "loadUIHistoryState", "addUIHistory", "showBlockingLayer", "hideBlockingLayer",
        "showMenuOverlay", "hideMenuOverlay", "applyApplicationTheme",
    ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            !IUIManagerWorker._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export interface DialogButtonDefinition {
    // LocKey popiseku tlacitka
    locKey: string;
    // jsetli tlacitko slouzi k opakovanemu spusteni akce - okno se nebude zavirat
    repeatUsingButton: boolean;
    // akce na tlacitko
    click: () => void;

    // jakykoli kontrol ktery se ma vytvorit namisto tlacitka - nahrazuje predchzejici hodnoty
    control: HTMLElement;

    // zakáže/povolí tlačítko pokud je na okně nastavena ValidationMessage
    enableOnValidationMessage: boolean;
}

/**
 * Rozhraní představující tovární funkci vytvářející hlavní menu aplikace
 * @includeToDoc
 */
export interface IMainMenuFactory {
    (): IMainMenu;
}

/**
 * Property sloužící pro přístup k UIManager
 */
export class UIManager {
    /**
     * Instance v kontextu současného okna
     */
    public static get instance(): IUIManagerWorker {
        return {} as any;
    }
}

InjectedProperty.bindProperty(UIManager, "instance", IUIManagerWorker);
GlobalContext.registerType(UIManager);

export interface UIHistoryState {
    type: string;
}

export interface UIHistoryStateForm extends UIHistoryState {
    formName: string;
    formID: string;
}

export interface UIHistoryStateFrame extends UIHistoryStateForm {
    frameName: string;
}
